import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BookMarked, BookOpen, FileText, FlaskConical, Zap, Sparkles, ScrollText } from 'lucide-react';

const STYLE = {
  library: { label: 'Library', icon: BookMarked, cls: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  manual: { label: 'Manual', icon: BookOpen, cls: 'bg-sky-50 text-sky-800 border-sky-300' },
  sop: { label: 'SOP', icon: ScrollText, cls: 'bg-indigo-50 text-indigo-800 border-indigo-300' },
  jha: { label: 'From JHA', icon: FileText, cls: 'bg-amber-50 text-amber-900 border-amber-300' },
  sds: { label: 'SDS', icon: FlaskConical, cls: 'bg-teal-50 text-teal-800 border-teal-300' },
  loto: { label: 'LOTO', icon: Zap, cls: 'bg-violet-50 text-violet-800 border-violet-300' },
  ai: { label: 'AI-generated', icon: Sparkles, cls: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300' },
};

const Chip = ({ kind, text, detail, testid }) => {
  const s = STYLE[kind] || STYLE.library;
  const Icon = s.icon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors hover:brightness-95 ${s.cls}`}
        >
          <Icon className="h-3 w-3" />
          {text}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs leading-relaxed" data-testid={`${testid}-detail`}>
        {detail}
      </PopoverContent>
    </Popover>
  );
};

/**
 * Per-row provenance badges. Multiple sources render multiple badges; a sourced row that
 * AI added new safety content to keeps its source badge AND shows "AI-generated".
 */
export const JsaSourceBadges = ({ hazard, index = 0 }) => {
  const provenance = hazard?.provenance || [];
  const isAi = hazard?.aiGenerated || hazard?.aiAugmented;
  if (provenance.length === 0 && !isAi) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`hazard-source-badges-${index}`}>
      {provenance.map((p, i) => {
        const kind = STYLE[p.type] ? p.type : 'library';
        const text = p.type === 'jha'
          ? `From JHA rev ${p.jhaRevision ?? '—'}`
          : STYLE[kind].label;
        return (
          <Chip
            key={`${p.ref}-${i}`}
            kind={kind}
            text={text}
            testid={`hazard-${index}-badge-${p.type}-${i}`}
            detail={
              <div className="space-y-1">
                <p className="font-semibold text-slate-900">{p.title || 'Source document'}</p>
                {p.type === 'jha' && (
                  <p className="text-slate-600">
                    Pre-Construction JHA · revision {p.jhaRevision ?? '—'} · status {p.status || '—'}
                  </p>
                )}
                {p.type === 'library' && (
                  <p className="text-slate-600">Approved learning library · revision {p.revision ?? 1}</p>
                )}
                {(p.type === 'manual' || p.type === 'sop') && (
                  <p className="text-slate-600">
                    {p.category ? `Category: ${p.category}` : 'Company safety manual / SOP'}
                    {p.version ? ` · v${p.version}` : ''}
                  </p>
                )}
                {p.type === 'loto' && <p className="text-slate-600">LOTO SOP · {p.equipmentName}</p>}
                {p.type === 'sds' && <p className="text-slate-600">Safety Data Sheet</p>}
                {hazard.sourceNote && <p className="text-slate-600 italic">“{hazard.sourceNote}”</p>}
                {hazard.verbatimFromSource && (
                  <p className="text-emerald-700">Control text reused exactly as approved.</p>
                )}
              </div>
            }
          />
        );
      })}

      {isAi && (
        <Chip
          kind="ai"
          text="AI-generated"
          testid={`hazard-${index}-badge-ai`}
          detail={
            <div className="space-y-1">
              <p className="font-semibold text-slate-900">
                {hazard.aiGenerated ? 'Developed by AI' : 'AI added content to a sourced row'}
              </p>
              <p className="text-slate-600">
                No company record covered this, so {hazard.aiModel || 'the safety model'} developed it.
                Review and edit before you accept — once accepted it becomes approved library content.
              </p>
              {hazard.aiRationale && <p className="text-slate-600 italic">{hazard.aiRationale}</p>}
            </div>
          }
        />
      )}
    </div>
  );
};

export default JsaSourceBadges;
